<template>
	<div ref="triggerWrapper">
		<PopoverRoot v-model:open="isOpen">
			<PopoverAnchor>
				<slot name="trigger" :isOpen="isOpen" :toggle="toggle">
					<PopoverTrigger as-child>
						<button
							type="button"
							class="btn btn-sm btn-ghost btn-circle bg-base-100"
						>
							<ListBulletIcon class="w-4 h-4"/>
						</button>
					</PopoverTrigger>
				</slot>
			</PopoverAnchor>
			<PopoverPortal>
				<PopoverContent
					:side="side"
					:side-offset="5"
					:align="align"
					:align-offset="-16"
					:collision-padding="16"
					:avoid-collisions="true"
					sticky="always"
					class="bg-white rounded-lg shadow-xl w-96 sm:max-w-md flex flex-col p-4 max-h-[min(66vh,var(--reka-popover-content-available-height))] z-50"
				>
					<!-- Header -->
					<div class="flex justify-between items-center mb-4 flex-shrink-0">
						<div class="font-semibold text-lg">{{ computedTitle }}</div>
						<!-- <PopoverClose
							class="btn btn-sm btn-ghost btn-circle"
							aria-label="Close"
						>
							<XMarkIcon class="cursor-pointer w-5 h-5" />
						</PopoverClose> -->
					</div>

					<!-- Main content area -->
					<div class="relative flex-1 min-h-0 flex flex-col overflow-x-hidden">
						<!-- Agenda context -->
						<!-- <div v-if="(context === 'agenda' || context === 'agenda-overview') && !isSavedToLibrary" class="flex-shrink-0"> -->
						<div v-if="!lessonPlan.isSavedInLib" class="flex-shrink-0 pb-6 relative">
							<p>
								{{ $t("#instruction: lesson plan not saved in library") }}
							</p>
							<div class="flex justify-center my-6">
								<button type="button" class="btn btn-primary" @click="saveToLibrary" :disabled="savingToLibrary">
									<span v-show="savingToLibrary" class="loading loading-spinner mr-2"></span>
									<PlusIcon class="w-5 h-5 mr-2" />
									{{ $t("Add to library") }}
								</button>
							</div>
						</div>

						<!-- Collections section -->
						<template v-else>

							<!-- Success message after saving to library -->
							<Transition name="fade">
							<div v-show="showSaveToLibSuccessMessage" class="absolute z-20 bg-success/90 rounded-lg flex flex-col items-center justify-center gap-3 p-4 mb-6 flex-shrink-0">
								<div class="flex items-center gap-2">
									<HandThumbUpIcon class="w-8 h-8 flex-shrink-0" />
									<div>{{ $t("Saved") }}!</div>
								</div>
								<p class="text-sm">
									{{ $t("You can now add your lesson plan to one or more collections in your library if you like.") }}
								</p>
							</div>
							</Transition>

							<div class="flex-1 min-h-40 overflow-y-auto overflow-x-hidden pr-2 pb-12" @scroll.stop>
								<!-- Create + Sort toolbar -->
								<div class="sticky top-0 z-10 pb-4 pt-2 flex-shrink-0 bg-white/50 backdrop-blur-sm">
									<div class="flex justify-between items-center pr-2" :class="{ 'opacity-0 pointer-events-none': showCreateInput }">
										<SortableHeader property="name" :order="localOrder" @change="handleOrderChange" />
										<button
											type="button"
											class="btn btn-primary btn-xs btn-circle"
											@click="onCreateClick"
										>
											<PlusIcon class="w-4 h-4" />
										</button>
									</div>
									<Transition name="slide-fade">
										<LessonPlanCollectionEditor
											v-if="showCreateInput"
											class="absolute inset-x-0 top-0 z-10"
											@requestUnmount="showCreateInput = false"
											@created="onCollectionCreated"
										/>
									</Transition>
								</div>

								<!-- Empty state -->
								<div v-if="!isLoading && !displayCollections.length" class="text-center text-base-content-light italic text-sm mb-12">{{ $t('No collections created yet') }}</div>

								<!-- Loading state -->
								<div v-else-if="isLoading" class="flex justify-center py-4" :class="{ 'opacity-40 pointer-events-none': showCreateInput }">
									<Skeleton content-type="userlist" :items="5" :withThumb="false" :context="'menu'" />
								</div>

								<!-- Collection items -->
								<ul v-else class="flex flex-col gap-2" :class="{ 'opacity-40 pointer-events-none': showCreateInput }">
									<li v-for="(item, index) in displayCollections" :key="item.id">
										<LessonPlanCollectionsListItem
											:collection="item"
											:showMarker="true"
											:showCheckbox="true"
											:checked="isSelected(item)"
											:loading="savingCollectionIds.has(item.id)"
											@click="toggleCollection(item)"
										/>
										<div
											v-if="shouldShowDividerAfter(index)"
											class="my-2 border-b border-base-100"
										></div>
									</li>
								</ul>
							</div>
						</template>
					</div>

					<!-- Footer -->
					<div class="absolute bottom-0 left-0 z-10 w-full flex justify-end bg-white/50 backdrop-blur-sm border-t border-base-200 shrink-0">
						<PopoverClose
							class="flex items-center p-1 justify-end mr-2"
							aria-label="Close"
						>
							<button type="button" class="btn btn-ghost">
								{{ $t("Done") }}
							</button>
						</PopoverClose>
					</div>
				</PopoverContent>
			</PopoverPortal>
		</PopoverRoot>
	</div>
</template>

<script lang="ts">
import { XMarkIcon, PlusIcon, HandThumbUpIcon, ListBulletIcon } from "@heroicons/vue/24/outline";
import { mapStores } from "pinia";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag";
import LessonPlanCollectionsListItem from "@/components/LessonPlanCollectionsListItem.vue";
import SortableHeader from "@/components/ui/SortableHeader.vue";
import Skeleton from "@/components/ui/Skeleton.vue";
import LessonPlanCollectionEditor from "@/components/LessonPlanCollectionEditor.vue";
import { PopoverArrow, PopoverClose, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger, PopoverAnchor } from 'reka-ui'
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model";
import { OrderDirection, OrderParameter } from "@/models/OrderParameter.model";

export default {
	name: "LessonPlanCollectionsSelector",

	emits: ["changed"],

	props: {
		lessonPlan: {
			type: Object as any,
			required: true,
		},
		// context: {
		// 	type: String,
		// 	default: "library", // 'library','agenda', 'discover' and 'agenda-library'
		// 	// there is also 'agenda-overview' to handle the case when the selector
		// 	// is used in the agenda itself and not in the drawer in agenda context
		// },
		title: {
			type: String,
		},
		align: {
			type: String,
			default: "end",
			validator: (value: string) => ["start", "center", "end"].includes(value),
		},
		side: {
			type: String,
			default: "bottom",
			validator: (value: string) => ["top", "bottom", "left", "right"].includes(value),
		},
	},

	components: {
		XMarkIcon,
		PlusIcon,
		HandThumbUpIcon,
		ListBulletIcon,
		LessonPlanCollectionsListItem,
		SortableHeader,
		Skeleton,
		LessonPlanCollectionEditor,
		PopoverArrow,
		PopoverClose,
		PopoverContent,
		PopoverPortal,
		PopoverRoot,
		PopoverTrigger,
		PopoverAnchor,
	},

	data() {
		return {
			isOpen: false,
			isLoading: false,
			showCreateInput: false,
			savingCollectionIds: new Set<string>(),
			savingToLibrary: false,
			showSaveToLibSuccessMessage: false,
			snapshotCollections: null as any[] | null,
			localOrder: new OrderParameter('name', OrderDirection.ASC),
		};
	},

	computed: {
		...mapStores(useLessonPlanCollectionsStore, useLessonPlansStore),

		computedTitle(): string {
			return this.title || this.$t("Add to library collections");
		},

		/**
		 * Store items partitioned into selected-first, then unselected.
		 * Sorting within each group is handled by the store.
		 */
		displayCollections(): any[] {
			const collections = this.snapshotCollections ?? this.lessonPlanCollectionsStore.getItems(false, this.localOrder);
			return collections.filter((l: any) => l?.id && l?.name);
		},
	},

	watch: {
		isOpen(newVal: boolean) {
			if (newVal) {
				this.initialize();
			} else {
				this.showSaveToLibSuccessMessage = false;
			}
		},
	},

	methods: {
		toggle() {
			this.isOpen = !this.isOpen;
		},

		open() {
			this.isOpen = true;
			this.snapshotCollections = null;
			this.initialize();
		},

		async initialize() {
			this.isLoading = true;
			try {
				await this.lessonPlanCollectionsStore.loadOnce();

				// Partition once at open time, then freeze
				const collections = this.lessonPlanCollectionsStore.getItems(false, this.localOrder);

				const selected = collections
					.filter((l: LessonPlanCollection) => l?.id && l?.name && this.isSelected(l))
					.map((l: LessonPlanCollection) => {
						l._selected = true; // add a temporary property to keep track of selection in the UI
						return l;
					});

				const unselected = collections
					.filter((l: LessonPlanCollection) => l?.id && l?.name && !this.isSelected(l))
					.map((l: LessonPlanCollection) => {
						l._selected = false;
						return l;
					});

				this.snapshotCollections = [...selected, ...unselected];
			} catch (e) {
				console.warn("Failed to load lesson plan collections:", e);
			}
			this.isLoading = false;
		},

		isSelected(item: any): boolean {
			return (this.lessonPlan?.collectionIds ?? []).includes(item.id);
		},

		shouldShowDividerAfter(index: number): boolean {
			const collections = this.snapshotCollections ?? [];
			const current = collections[index];
			const next = collections[index + 1];
			return !!current?._selected && !!next && !next._selected;
		},

		async toggleCollection(item: any) {
			if (!this.lessonPlan?.id || !item?.id) return;

			const listId = item.id;
			const isCurrentlySelected = this.isSelected(item);

			// Mark as saving (force reactivity via reassignment)
			this.savingCollectionIds.add(listId);
			this.savingCollectionIds = new Set(this.savingCollectionIds);

			try {
				if (isCurrentlySelected) {
					await this.lessonPlanCollectionsStore.removeLessonPlanFromCollection(item, this.lessonPlan);
				} else {
					await this.lessonPlanCollectionsStore.addLessonPlanToCollection(item, this.lessonPlan);
				}
				// check the current filter of the LessonPlansStore if the collection being removed/added is currently filtered on,
				// if so reload the lesson plans to reflect the change, otherwise we would still see the removed lesson plan in the list until next reload
				const currentLessonPlanFilter = this.lessonPlansStore.getCurrentFilter();
				if (currentLessonPlanFilter.collectionIds?.includes(item.id)) {
					await this.lessonPlansStore.load();
				}
				this.$emit("changed", this.lessonPlan.collectionIds);
			} catch (e: any) {

				if (e instanceof ApiErrorMessageBag) {
					this.$notify({
						text: e.toString() || this.$t("An error occurred while updating the collection"),
						type: "error",
					});
				} else {
					console.error(e);
					this.$notify({
						text: this.$t("An error occurred while updating the collection"),
						type: "error",
					});
				}

			} finally {
				this.savingCollectionIds.delete(listId);
				this.savingCollectionIds = new Set(this.savingCollectionIds);
			}
		},

		async saveToLibrary() {
			if (!this.lessonPlan) return;

			this.savingToLibrary = true;

			try {
				// @todo check if this is correct
				// scenarios:
				// 1. this is a brand new lesson plan -> save!
				// 2. this is a lesson plan owned by someone else -> addToLibrary
				// 3. this is a lesson plan that is attached to a lesson item, but is not in a library -> save
				// 4. more???
				if (this.lessonPlan.id) {
					await this.lessonPlansStore.addToLibrary(this.lessonPlan);
				} else {
					await this.lessonPlansStore.save(this.lessonPlan);
				}

				this.lessonPlan.isSavedInLib = true;
				this.showSaveToLibSuccessMessage = true;

				// Auto-hide success message after 3 seconds
				setTimeout(() => {
					this.showSaveToLibSuccessMessage = false;
				}, 3000);

				this.$notify({
					text: this.$t("Lesson plan saved to library"),
					type: "success",
				});
			} catch (e: any) {
				if (e instanceof ApiErrorMessageBag) {
					this.$notify({
						text: e.toString() || this.$t("Failed to save to library"),
						type: "error",
					});
				} else {
					console.error(e);
					this.$notify({
						text: this.$t("Failed to save to library"),
						type: "error",
					});
				}
			} finally {
				this.savingToLibrary = false;
			}
		},

		onCreateClick() {
			this.showCreateInput = true;
		},

		async onCollectionCreated(payload: { collection: LessonPlanCollection, context: any }) {
			this.showCreateInput = false;

			const createdCollection = payload?.collection;
			if (!createdCollection?.id || !this.lessonPlan?.id) return;
			if (!this.snapshotCollections || this.snapshotCollections.find((c: any) => c.id === createdCollection.id)) return;

			const items = this.lessonPlanCollectionsStore.getItems(false, this.localOrder);
			const selected = items.filter((l: any) => this.isSelected(l) || l.id === createdCollection.id).map((l: any) => ({ ...l, _selected: true }));
			const unselected = items.filter((l: any) => !this.isSelected(l) && l.id !== createdCollection.id).map((l: any) => ({ ...l, _selected: false }));
			this.snapshotCollections = [...selected, ...unselected];

			await this.toggleCollection(createdCollection);
		},

		handleOrderChange(newOrder: OrderParameter) {
			this.localOrder = newOrder;

			if (!this.snapshotCollections) return;

			const selectedIds = new Set(
				this.snapshotCollections
					.filter((c: any) => c._selected)
					.map((c: any) => c.id)
			);

			const sortedItems = this.lessonPlanCollectionsStore.getItems(false, newOrder);
			const selected = sortedItems.filter((c: any) => selectedIds.has(c.id));
			const unselected = sortedItems.filter((c: any) => !selectedIds.has(c.id));

			this.snapshotCollections = [...selected, ...unselected];
		},
	},
};
</script>
